'use client'

import type { PayloadMeUser } from '@payloadcms/admin-bar'

import { cn } from '@/utilities/ui'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useState } from 'react'

import type { AdminBarCollectionSlug } from './AdminBarDocumentContext'
import { useAdminBarDocument } from './AdminBarDocumentContext'

const ADMIN_BAR_EDIT_KEY: Record<AdminBarCollectionSlug, string> = {
  posts: 'editPost',
  pages: 'editPage',
  categories: 'editCategory',
  tags: 'editTag',
}

const baseClass = 'admin-bar'

const adminPath = '/admin'
const apiPath = '/api'
const authCollectionSlug = 'users'

const Title: React.FC = () => {
  const t = useTranslations('AdminBar')
  return <span>{t('dashboard')}</span>
}

export const AdminBar: React.FC<{
  adminBarProps?: { preview?: boolean }
}> = (props) => {
  const preview = props.adminBarProps?.preview ?? false
  const router = useRouter()
  const t = useTranslations('AdminBar')
  const { doc } = useAdminBarDocument()

  const [user, setUser] = useState<PayloadMeUser>(undefined)

  useEffect(() => {
    const fetchMe = async () => {
      try {
        /** Mesmo host da página (evita localhost vs 127.0.0.1 vs `NEXT_PUBLIC_SERVER_URL` no SSR). */
        const res = await fetch(`${apiPath}/${authCollectionSlug}/me`, {
          credentials: 'include',
          method: 'get',
        })
        const data = await res.json()
        setUser(data.user ?? null)
      } catch {
        setUser(null)
      }
    }
    void fetchMe()
  }, [])

  const onPreviewExit = useCallback(() => {
    fetch('/next/exit-preview').then(() => {
      router.push('/')
      router.refresh()
    })
  }, [router])

  if (!user?.id) return null

  const editHref =
    doc ?
      `${adminPath}/collections/${doc.collectionSlug}/${encodeURIComponent(String(doc.id))}`
    : null
  const editLabel = doc ? t(ADMIN_BAR_EDIT_KEY[doc.collectionSlug]) : null

  const createPostHref = `${adminPath}/collections/posts/create`
  const createPageHref = `${adminPath}/collections/pages/create`
  const dashboardHref = adminPath
  const logoutHref = `${adminPath}/logout`

  return (
    <div
      className={cn(
        baseClass,
        'relative z-50 bg-black py-2 text-white max-md:hidden md:block',
      )}
    >
      <div className="container">
        <div
          id="payload-admin-bar"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 text-sm text-white"
          style={{
            alignItems: 'center',
            backgroundColor: 'transparent',
            boxSizing: 'border-box',
            minWidth: 0,
            position: 'relative',
            width: '100%',
          }}
        >
          {/* Esquerda: Dashboard + Novo + Editar documento (quando aplicável) */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 font-medium">
            <a className="shrink-0 text-white no-underline hover:underline" href={dashboardHref}>
              <Title />
            </a>

            <div className="group relative z-10 inline-flex flex-col items-start">
              <span
                className="cursor-pointer whitespace-nowrap text-white underline-offset-2 select-none hover:underline"
                aria-haspopup="menu"
              >
                {t('new')}
              </span>
              {/*
                Painel logo abaixo de «Novo» (top-full). pt-1 = faixa invisível para manter o hover
                ao mover o cursor do rótulo para o menu.
              */}
              <div
                className="border-border pointer-events-none invisible absolute left-0 top-full z-20 flex min-w-full flex-col pt-1 opacity-0 transition-opacity delay-100 duration-150 group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100 group-hover:delay-0"
                role="menu"
              >
                <div className="min-w-44 rounded-md border bg-neutral-950 py-1 whitespace-nowrap shadow-lg ring-1 ring-white/10">
                  <a
                    className="block px-3 py-2 text-white no-underline hover:bg-white/10"
                    href={createPostHref}
                    role="menuitem"
                  >
                    {t('newPost')}
                  </a>
                  <a
                    className="block px-3 py-2 text-white no-underline hover:bg-white/10"
                    href={createPageHref}
                    role="menuitem"
                  >
                    {t('newPage')}
                  </a>
                </div>
              </div>
            </div>

            {editHref && editLabel ? (
              <a className="shrink-0 text-white no-underline hover:underline" href={editHref}>
                {editLabel}
              </a>
            ) : null}
          </div>

          {/* Direita: sair do preview */}
          <div
            className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-3 gap-y-1 font-medium"
            style={{ marginLeft: 8, marginRight: 10 }}
          >
            {preview ? (
              <button
                type="button"
                className="cursor-pointer border-0 bg-transparent p-0 text-inherit underline"
                onClick={onPreviewExit}
              >
                {t('exitPreview')}
              </button>
            ) : null}
          </div>

          <a className="shrink-0 text-white no-underline hover:underline" href={logoutHref}>
            {t('logout')}
          </a>
        </div>
      </div>
    </div>
  )
}
